import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import WeeklyCraDialog from "@/components/WeeklyCraDialog";
import { monthMatrix } from "@/lib/timesheetUtils";
import ImpersonationBanner from "@/components/timesheet/ImpersonationBanner";
import TimesheetHeader from "@/components/timesheet/TimesheetHeader";
import LeaveBalanceBanner from "@/components/timesheet/LeaveBalanceBanner";
import SelectorBar from "@/components/timesheet/SelectorBar";
import MissionEnvelopeOverflowBanner from "@/components/timesheet/MissionEnvelopeOverflowBanner";
import MonthSummaryBanner from "@/components/timesheet/MonthSummaryBanner";
import CalendarGrid from "@/components/timesheet/CalendarGrid";
import OnCallDialog from "@/components/timesheet/OnCallDialog";
import DayActionSheet from "@/components/timesheet/DayActionSheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const today = new Date();

export default function Timesheet() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const viewedUserId = searchParams.get("user_id"); // when set, viewing/editing on behalf of another user
  const isImpersonating = !!viewedUserId && viewedUserId !== user?.user_id;
  // Iter-123: CEO/ADMIN can edit on behalf. MANAGER can only submit on behalf (not edit).
  const canEditOtherRole = user?.role === "CEO" || user?.role === "ADMIN";
  const canSubmitOtherRole = user?.role === "CEO" || user?.role === "ADMIN" || user?.role === "MANAGER";
  // Iter-123: when CEO impersonates, DEFAULT to read-only until the explicit "Edit" button
  // is clicked (which requires entering a reason for the edit).
  const [editingOnBehalf, setEditingOnBehalf] = useState(null); // { reason, beforeEntries } | null
  const canEditOther = isImpersonating && canEditOtherRole && !!editingOnBehalf;
  const isReadOnly = isImpersonating && !canEditOther;

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedMission, setSelectedMission] = useState("");
  const [selectedActivity, setSelectedActivity] = useState(user?.role === "GESTIONNAIRE" ? "DAILY" : "MISSION");
  const [missions, setMissions] = useState([]);
  const [data, setData] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockMode, setUnlockMode] = useState("GLOBAL");
  const [unlockMissions, setUnlockMissions] = useState([]);
  const [unlockReason, setUnlockReason] = useState("");
  const [holidays, setHolidays] = useState({}); // {YYYY-MM-DD: {name, country}}
  const [submitDialog, setSubmitDialog] = useState(null); // { dates, reasons, holidays }
  const [leaveBalance, setLeaveBalance] = useState(null);
  // Iter-80: Weekly CRA modal state + per-year submissions cache for badging.
  const [weeklyCraDialog, setWeeklyCraDialog] = useState(null);
  const [weeklySubs, setWeeklySubs] = useState([]);
  // Iter-203 — subset of assigned missions with weekly_cra_enabled=true. Drives
  // the Weekly CRA CTA visibility on the calendar and the dialog mission picker.
  const [weeklyCraMissions, setWeeklyCraMissions] = useState([]);
  // Iter-115: mobile bottom sheet for day actions (+2h / +8h / delete entry).
  const [daySheet, setDaySheet] = useState(null); // { day, dateStr, dateLabel } | null

  const load = async () => {
    const craUrl = isImpersonating ? `/cra/${year}/${month}?user_id=${viewedUserId}` : `/cra/${year}/${month}`;
    const missionsUrl = isImpersonating
      ? `/missions?assigned_to=${viewedUserId}`
      : "/missions?assigned_to_me=true";
    const wkAvailUrl = isImpersonating
      ? `/weekly-cra/available-missions?user_id=${encodeURIComponent(viewedUserId)}`
      : "/weekly-cra/available-missions";
    // Iter-228x-bis — When viewing/editing on behalf, request holidays for the
    // impersonated user's mission countries (not the CEO's own).
    const holidaysUrl = isImpersonating
      ? `/holidays?year=${year}&scope=mine&user_id=${encodeURIComponent(viewedUserId)}`
      : `/holidays?year=${year}&scope=mine`;
    try {
      const [m, c, h, lb, wkSubs, wkAvail] = await Promise.all([
        api.get(missionsUrl).catch(() => ({ data: [] })),
        api.get(craUrl),
        api.get(holidaysUrl),
        isImpersonating ? Promise.resolve({ data: null }) : api.get(`/me/leave-balance?year=${year}`).catch(() => ({ data: null })),
        isImpersonating ? api.get(`/weekly-cra/submissions?year=${year}&user_id=${encodeURIComponent(viewedUserId)}`).catch(() => ({ data: [] })) : api.get(`/weekly-cra/submissions?year=${year}`).catch(() => ({ data: [] })),
        api.get(wkAvailUrl).catch(() => ({ data: [] })),
      ]);
      setMissions(m.data);
      setData(c.data);
      setHolidays(Object.fromEntries((h.data || []).map((x) => [x.date, { name: x.name, country: x.country || null }])));
      setLeaveBalance(lb.data);
      setWeeklySubs(wkSubs.data || []);
      setWeeklyCraMissions(wkAvail.data || []);
      if (!selectedMission && m.data.length > 0) setSelectedMission(m.data[0].mission_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load this timesheet");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, viewedUserId]);

  const cells = useMemo(() => monthMatrix(year, month), [year, month]);
  const entriesByDay = useMemo(() => {
    const map = {};
    (data?.entries || []).forEach((e) => {
      const d = parseInt(e.date.split("-")[2], 10);
      (map[d] = map[d] || []).push(e);
    });
    return map;
  }, [data]);

  // Iter-35: real-time per-chip CONGE overflow detection.
  const congeOverflowMap = useMemo(() => {
    const out = {};
    if (!leaveBalance?.applies) return out;
    const allowance = Number(leaveBalance.allowance || 0);
    if (allowance <= 0) return out;
    const sortedConge = (data?.entries || [])
      .filter((e) => e.activity_type === "CONGE")
      .sort((a, b) => a.date.localeCompare(b.date) || a.entry_id.localeCompare(b.entry_id));
    if (sortedConge.length === 0) return out;
    const currentMonthConge = sortedConge.reduce((acc, e) => acc + (Number(e.hours) || 0) / 8, 0);
    const baseline = data?.submission?.status === "APPROVED"
      ? Math.max(0, Number(leaveBalance.taken || 0) - currentMonthConge)
      : Number(leaveBalance.taken || 0);
    let cum = baseline;
    for (const e of sortedConge) {
      cum += (Number(e.hours) || 0) / 8;
      if (cum > allowance + 1e-9) out[e.entry_id] = true;
    }
    return out;
  }, [data, leaveBalance]);

  // Iter-79: per-mission days-this-month (live, mutates with every entry edit).
  const missionMonthDays = useMemo(() => {
    const out = {};
    (data?.entries || []).forEach((e) => {
      if (e.activity_type !== "MISSION" || !e.mission_id) return;
      out[e.mission_id] = (out[e.mission_id] || 0) + (Number(e.hours) || 0) / 8;
    });
    return out;
  }, [data]);

  // Iter-79: per-activity days-this-month.
  const activityMonthDays = useMemo(() => {
    const out = {};
    (data?.entries || []).forEach((e) => {
      const k = e.activity_type;
      if (!k) return;
      out[k] = (out[k] || 0) + (Number(e.hours) || 0) / 8;
    });
    return out;
  }, [data]);

  // Iter-79: working days in the displayed month — Mon..Fri minus public holidays.
  const workingDaysInMonth = useMemo(() => {
    const holidaySet = new Set((data?.holidays || []).map((h) => h.date));
    let n = 0;
    for (let d = 1; d <= 31; d++) {
      const dt = new Date(year, month - 1, d);
      if (dt.getMonth() !== month - 1) break;
      const dow = dt.getDay();
      if (dow === 0 || dow === 6) continue;
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (holidaySet.has(iso)) continue;
      n += 1;
    }
    return n;
  }, [year, month, data]);

  const submission = data?.submission;
  // Iter-33: month is permanently locked once all client invoices for missions worked that month are paid.
  const monthSettled = !!data?.month_settled;
  const lockedByStatus = submission?.status === "SUBMITTED" || submission?.status === "APPROVED";
  const locked = isReadOnly || (lockedByStatus && !canEditOther);
  const canRequestUnlock = !isImpersonating && !monthSettled
    && submission && submission.status !== "DRAFT" && submission.status !== "UNLOCK_APPROVED";

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  // Iter-110/115: cap left/right-click increments to whatever's left of the user envelope.
  // When the call comes from the mobile bottom sheet, the user can override the activity
  // and mission directly in the sheet (passed via overrideActivity / overrideMissionId).
  const onDayClick = async (d, increment = 2, overrideActivity = null, overrideMissionId = null) => {
    if (!d || locked) return;
    const effActivity = overrideActivity || selectedActivity;
    const effMissionId = effActivity === "MISSION"
      ? (overrideMissionId || (overrideActivity ? null : selectedMission))
      : null;
    let effectiveIncrement = increment;
    if (effActivity === "MISSION" && effMissionId) {
      const m = missions.find((mm) => mm.mission_id === effMissionId);
      const envelope = Number(m?.envelope_jh) || 0;
      const consumed = Number(m?.consumed_jh) || 0;
      if (envelope > 0) {
        const remainingHours = Math.max(0, (envelope - consumed) * 8);
        if (remainingHours <= 0) {
          toast.info("Budget JH consommé à 100% — saisie bloquée, contactez votre manager.");
          return;
        }
        effectiveIncrement = Math.min(increment, remainingHours);
      }
    }
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const body = {
      date: dateStr,
      activity_type: effActivity,
      mission_id: effActivity === "MISSION" ? effMissionId || null : null,
      increment_hours: effectiveIncrement,
      ...(canEditOther ? { user_id: viewedUserId } : {}),
    };
    if (effActivity === "MISSION" && !effMissionId) {
      toast.error("Select a mission first"); return;
    }
    try {
      await api.post("/cra/entries/increment", body);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add hours");
    }
  };

  const submitMonth = async (onCallReasons = {}) => {
    try {
      const url = (isImpersonating && canSubmitOtherRole)
        ? `/cra/submit/${year}/${month}?user_id=${viewedUserId}`
        : `/cra/submit/${year}/${month}`;
      await api.post(url, { on_call_reasons: onCallReasons });
      toast.success(isImpersonating ? "CRA submitted on behalf of the user" : "CRA submitted");
      setSubmitDialog(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "ON_CALL_REASON_REQUIRED") {
        const reasons = {}; (detail.dates || []).forEach((d) => { reasons[d] = ""; });
        setSubmitDialog({ dates: detail.dates || [], reasons, holidays: detail.holidays || {} });
        return;
      }
      toast.error(detail || "Submit failed");
    }
  };

  // Iter-123/124: on click "Edit", unlock immediately + snapshot entries. The reason is
  // captured AT SAVE TIME (when the CEO actually has a diff to justify), via a dialog.
  const [editReasonOpen, setEditReasonOpen] = useState(false);
  const [editReasonDraft, setEditReasonDraft] = useState("");
  const startEdit = () => {
    const beforeEntries = (data?.entries || []).map((e) => ({
      date: e.date, activity_type: e.activity_type,
      mission_id: e.mission_id || null, hours: Number(e.hours) || 0,
    }));
    setEditingOnBehalf({ reason: "", beforeEntries });
    toast.message("Edit mode enabled", { description: "A reason will be requested when you save." });
  };
  const cancelEdit = () => {
    setEditingOnBehalf(null);
    toast.message("Edit mode cancelled", { description: "Changes already saved are not rolled back." });
  };
  const requestSaveEdit = () => {
    if (!editingOnBehalf) return;
    setEditReasonDraft("");
    setEditReasonOpen(true);
  };
  const confirmSaveEdit = async (silent = false) => {
    if (!editingOnBehalf) return;
    if (!editReasonDraft.trim()) {
      toast.error("A reason is required");
      return;
    }
    try {
      const r = await api.post("/cra/on-behalf/notify", {
        user_id: viewedUserId,
        year, month,
        reason: editReasonDraft.trim(),
        before: editingOnBehalf.beforeEntries,
        silent,
      });
      if (r.data?.no_changes) {
        toast.message("No changes detected — no notification sent.");
      } else if (r.data?.silent) {
        toast.success("Changes saved silently — user NOT notified");
      } else {
        toast.success("Changes saved and user notified by email + in-app");
      }
      setEditingOnBehalf(null);
      setEditReasonOpen(false);
      setEditReasonDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteEntry = async (entry) => {
    if (locked) return;
    // Iter-110: no more confirm() — chip-click = delete.
    // Iter-228x-quinto: deletion is idempotent. A 404 from the backend means
    // the entry is already gone (typically because of a double-tap on the
    // delete button in the mobile DayActionSheet — touch devices easily
    // register two taps in quick succession). The user's intent — "make this
    // entry disappear" — is satisfied in both cases, so we suppress the
    // misleading "Entry not found" toast and just refresh the view.
    try {
      await api.delete(`/cra/entries/${entry.entry_id}`);
      await load();
    } catch (e) {
      if (e?.response?.status === 404) {
        await load();  // entry already deleted — refresh & swallow
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const sendUnlock = async () => {
    try {
      await api.post("/cra/unlock-request", {
        year, month, mode: unlockMode,
        mission_ids: unlockMode === "SELECTIVE" ? unlockMissions : [],
        reason: unlockReason,
      });
      toast.success("Unlock request sent");
      setUnlockOpen(false); setUnlockReason(""); setUnlockMissions([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    }
  };

  const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // Iter-228x-bis — Detect "unsaved" edit context: in impersonation edit mode,
  // each cell click already persists to the backend, but the "Save changes"
  // step is what records the reason + sends the diff email to the collaborator.
  // If the CEO clicks "Submit on behalf" with a non-empty diff (vs the snapshot
  // captured at edit start) AND hasn't yet clicked "Save changes", warn them.
  const hasUnsavedEditDiff = () => {
    if (!editingOnBehalf) return false;
    const before = editingOnBehalf.beforeEntries || [];
    const after = (data?.entries || []).map((e) => ({
      date: e.date, activity_type: e.activity_type,
      mission_id: e.mission_id || null, hours: Number(e.hours) || 0,
    }));
    if (before.length !== after.length) return true;
    const key = (e) => `${e.date}|${e.activity_type}|${e.mission_id || ""}|${e.hours}`;
    const bset = new Set(before.map(key));
    return after.some((e) => !bset.has(key(e)));
  };

  const submitOnBehalfGuarded = () => {
    // Read-only impersonation → submit directly.
    if (!editingOnBehalf) {
      submitMonth({});
      return;
    }
    // Edit mode WITHOUT a pending diff → also submit directly.
    if (!hasUnsavedEditDiff()) {
      submitMonth({});
      return;
    }
    // Edit mode WITH unsaved diff → warn first.
    toast.warning(
      "Edit on behalf — n'oubliez pas de sauvegarder avant de soumettre",
      {
        description:
          "Vos modifications sont persistées mais la notification (motif + diff) n'a pas encore été envoyée au collaborateur. Cliquez « Save changes » d'abord, ou « Submit anyway » pour soumettre sans notifier.",
        duration: 12000,
        action: {
          label: "Save first",
          onClick: () => requestSaveEdit(),
        },
        cancel: {
          label: "Submit anyway",
          onClick: () => submitMonth({}),
        },
      },
    );
  };

  // Iter-110/111/193: filter missions for the SELECTOR (not for color resolution).
  // Iter-193 — keep exhausted missions VISIBLE so the user can browse past months.
  // Only hide when the mission is CLOSED or its end_date is strictly before the
  // first day of the displayed month. Saisie is blocked at input time in onDayClick.
  const visibleMissions = useMemo(() => {
    const lastDayIso = (() => {
      const last = new Date(year, month, 0);
      return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
    })();
    const firstDayIso = `${year}-${String(month).padStart(2, "0")}-01`;
    return missions.filter((m) => {
      if (m.status === "CLOSED") return false;
      if (m.end_date && m.end_date < firstDayIso) return false;
      const sd = m.user_assignment_start_date;
      if (sd && lastDayIso < sd) return false;
      return true;
    });
  }, [missions, year, month]);

  // Iter-69: per-mission envelope overflow banner — only for the currently selected mission.
  const overflowMission = (() => {
    if (selectedActivity !== "MISSION" || !selectedMission) return null;
    const m = missions.find((x) => x.mission_id === selectedMission);
    if (!m) return null;
    const env = Number(m.envelope_jh || 0);
    const cons = Number(m.consumed_jh || 0);
    return env > 0 && cons > env + 1e-9 ? m : null;
  })();

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="timesheet-page">
      {isImpersonating && (
        <ImpersonationBanner
          canEditOtherRole={canEditOtherRole}
          canSubmitOtherRole={canSubmitOtherRole}
          editingOnBehalf={editingOnBehalf}
          onStartEdit={startEdit}
          onSaveEdit={requestSaveEdit}
          onCancelEdit={cancelEdit}
          onSubmitOnBehalf={submitOnBehalfGuarded}
          targetUser={data?.target_user}
          viewedUserId={viewedUserId}
          missionSubmitted={submission?.status === "SUBMITTED" || submission?.status === "APPROVED"}
        />
      )}

      <TimesheetHeader
        monthName={monthName}
        onPrev={prev}
        onNext={next}
        onSubmit={() => submitMonth({})}
        showSubmit={!locked && !isImpersonating}
        isApproved={submission?.status === "APPROVED"}
        monthSettled={monthSettled}
        showSettledBadge={monthSettled && !isImpersonating}
        settledSummary={data?.settled_summary}
        canRequestUnlock={canRequestUnlock}
        // Iter-124: inline collaborator search (CEO / ADMIN).
        showCollabSearch={canEditOtherRole}
        currentUserId={user.user_id}
        unlockOpen={unlockOpen}
        setUnlockOpen={setUnlockOpen}
        unlockMode={unlockMode}
        setUnlockMode={setUnlockMode}
        unlockMissions={unlockMissions}
        setUnlockMissions={setUnlockMissions}
        unlockReason={unlockReason}
        setUnlockReason={setUnlockReason}
        missions={missions}
        onSendUnlock={sendUnlock}
      />

      {leaveBalance?.applies && !isImpersonating && leaveBalance.allowance > 0 && (
        <LeaveBalanceBanner leaveBalance={leaveBalance} />
      )}

      {/* Iter-203 — Inform users that no mission has opted-in to the Weekly CRA
          workflow, so the "Send Weekly CRA" button is intentionally hidden. */}
      {!isImpersonating && missions.length > 0 && weeklyCraMissions.length === 0 && (
        <div
          className="mb-6 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900"
          data-testid="no-weekly-cra-banner"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 shrink-0">
            <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
          <div className="text-xs leading-relaxed">
            <div className="font-semibold mb-0.5">Weekly CRA non activé</div>
            Aucune de vos missions n'a activé le CRA hebdomadaire. Si un client le demande,
            contactez votre manager pour qu'il l'active dans les paramètres de la mission.
          </div>
        </div>
      )}

      {locked && (
        <div
          className="mb-6 flex items-center gap-3 px-5 py-4 bg-zinc-950 text-white rounded-lg"
          data-testid="locked-banner"
        >
          <Lock size={16} />
          <div className="text-sm">
            {submission?.status ? (
              <>This month is{" "}
                <span className="font-mono uppercase tracking-wider">{submission.status}</span>.
                {" "}No edits allowed unless unlocked.</>
            ) : (
              <>Read-only view — no edits allowed.</>
            )}
          </div>
        </div>
      )}

      <SelectorBar
        user={isImpersonating && data?.target_user ? { ...user, ...data.target_user } : user}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        selectedMission={selectedMission}
        setSelectedMission={setSelectedMission}
        visibleMissions={visibleMissions}
        missionMonthDays={missionMonthDays}
      />

      {overflowMission && <MissionEnvelopeOverflowBanner mission={overflowMission} />}

      <MonthSummaryBanner
        activityMonthDays={activityMonthDays}
        workingDaysInMonth={workingDaysInMonth}
        isFreelance={!!(isImpersonating && data?.target_user ? data.target_user.is_freelance : user.is_freelance)}
      />

      <div className="text-xs text-zinc-500 mb-2 font-mono">
        <span className="md:hidden">Tap day = open editor · Long-press = delete · Max 8h/mission/day (jusqu'à 16h sur plusieurs missions)</span>
        <span className="hidden md:inline">Click day = +2h · Double-click = +8h · Right-click = delete · Max 8h/mission/day (jusqu'à 16h sur plusieurs missions)</span>
      </div>

      <CalendarGrid
        year={year}
        month={month}
        cells={cells}
        today={today}
        missions={missions}
        entriesByDay={entriesByDay}
        holidays={holidays}
        weeklySubs={weeklySubs}
        selectedActivity={selectedActivity}
        selectedMission={selectedMission}
        isImpersonating={isImpersonating}
        locked={locked}
        user={isImpersonating && data?.target_user ? { ...user, ...data.target_user } : user}
        leaveBalance={leaveBalance}
        congeOverflowMap={congeOverflowMap}
        onDayClick={onDayClick}
        onDeleteEntry={deleteEntry}
        onOpenWeeklyCra={setWeeklyCraDialog}
        weeklyCraMissions={weeklyCraMissions}
        onOpenDaySheet={(d) => {
          if (!d) return;
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const label = new Date(year, month - 1, d).toLocaleDateString(undefined, {
            weekday: "long", year: "numeric", month: "long", day: "numeric",
          });
          setDaySheet({ day: d, dateStr, dateLabel: label });
        }}
      />

      {submission?.rejection_reason && (
        <div
          className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3"
          data-testid="rejection-reason"
        >
          <strong>Rejected:</strong> {submission.rejection_reason}
        </div>
      )}

      {/* Iter-80: Weekly CRA submission dialog */}
      {weeklyCraDialog && (
        <WeeklyCraDialog
          open={!!weeklyCraDialog}
          onOpenChange={(o) => { if (!o) setWeeklyCraDialog(null); }}
          missionId={weeklyCraDialog.missionId}
          year={weeklyCraDialog.isoYear}
          isoWeek={weeklyCraDialog.isoWeek}
          missionTitle={weeklyCraDialog.missionTitle}
          availableMissions={visibleMissions
            .filter((m) => weeklyCraMissions.some((w) => w.mission_id === m.mission_id))
            .map((m) => ({ mission_id: m.mission_id, title: m.title }))}
          impersonatedUserId={isImpersonating ? viewedUserId : null}
          impersonatedUserName={isImpersonating ? (data?.target_user?.name || data?.target_user?.email || viewedUserId) : null}
          onSent={() => {
            setWeeklyCraDialog(null);
            const url = isImpersonating
              ? `/weekly-cra/submissions?year=${year}&user_id=${encodeURIComponent(viewedUserId)}`
              : `/weekly-cra/submissions?year=${year}`;
            api.get(url)
              .then((r) => setWeeklySubs(r.data || []))
              .catch(() => {});
          }}
        />
      )}

      <OnCallDialog
        submitDialog={submitDialog}
        setSubmitDialog={setSubmitDialog}
        onConfirm={(reasons) => submitMonth(reasons)}
      />

      {/* Iter-124: motif required only at SAVE time (not at Edit click) */}
      <Dialog open={editReasonOpen} onOpenChange={setEditReasonOpen}>
        <DialogContent data-testid="edit-reason-dialog">
          <DialogHeader>
            <DialogTitle>Save changes on {data?.target_user?.name || viewedUserId}'s timesheet</DialogTitle>
            <DialogDescription>
              Please provide a reason for the modifications you made. An email with
              the before/after diff will be sent to the user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="edit-reason">Reason</Label>
            <Textarea
              id="edit-reason"
              data-testid="edit-reason-input"
              value={editReasonDraft}
              onChange={(e) => setEditReasonDraft(e.target.value)}
              placeholder="e.g. Correction of missing sick day + reallocation to training mission"
              rows={3}
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setEditReasonOpen(false)}
              className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md"
            >
              Cancel
            </button>
            {/* Iter-228x — Silent save (CEO only). Persists the audit log + entries
                but skips the email diff and in-app notification to the target. */}
            {user?.role === "CEO" && (
              <button
                onClick={() => confirmSaveEdit(true)}
                data-testid="edit-reason-confirm-silent"
                title="Enregistrer sans notifier le collaborateur (l'audit est conservé)"
                className="px-4 h-10 bg-white border border-zinc-300 text-zinc-700 text-sm rounded-md hover:bg-zinc-50 inline-flex items-center gap-2"
              >
                Save without notification
              </button>
            )}
            <button
              onClick={() => confirmSaveEdit(false)}
              data-testid="edit-reason-confirm"
              className="px-4 h-10 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 inline-flex items-center gap-2"
            >
              Save &amp; notify
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-115/116: mobile bottom sheet for day actions — full activity+mission picker + day arrows */}
      <DayActionSheet
        open={!!daySheet}
        onClose={() => setDaySheet(null)}
        day={daySheet?.day || 1}
        year={year}
        month={month}
        dateLabel={daySheet?.dateLabel || ""}
        daysInMonth={new Date(year, month, 0).getDate()}
        entries={daySheet ? (entriesByDay[daySheet.day] || []) : []}
        missions={missions}
        user={isImpersonating && data?.target_user ? { ...user, ...data.target_user } : user}
        canAdd={!locked}
        onChangeDay={(newDay) => {
          if (!newDay) return;
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(newDay).padStart(2, "0")}`;
          const label = new Date(year, month - 1, newDay).toLocaleDateString(undefined, {
            weekday: "long", year: "numeric", month: "long", day: "numeric",
          });
          setDaySheet({ day: newDay, dateStr, dateLabel: label });
        }}
        onAdd={async (hours, activity, missionId) => {
          if (!daySheet) return;
          await onDayClick(daySheet.day, hours, activity, missionId);
        }}
        onDelete={async (entry) => {
          await deleteEntry(entry);
        }}
      />
    </div>
  );
}
